import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, UserInfo } from '../services/auth.service';
import { setAuthToken } from '../lib/api';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserInfo | null;
  login: () => void;
  logout: () => void;
  getAccessToken: () => string | null;
  refreshAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<UserInfo | null>(null);

  useEffect(() => {
    // Check authentication status on mount
    checkAuth();
  }, []);

  const checkAuth = () => {
    setIsLoading(true);
    const authenticated = authService.isAuthenticated();
    setIsAuthenticated(authenticated);

    if (authenticated) {
      const userInfo = authService.getUserInfo();
      setUser(userInfo);

      // Set auth token for API requests (may be null if auth is disabled)
      const token = authService.getAccessToken();
      setAuthToken(token);

      // Log warning if auth is disabled
      const isAuthDisabled = import.meta.env.VITE_DISABLE_AUTH === 'true' || import.meta.env.VITE_DISABLE_AUTH === '1';
      if (isAuthDisabled) {
        console.warn('⚠️ Authentication is DISABLED (VITE_DISABLE_AUTH=true). This should only be used for development/testing!');
      }
    } else {
      // Clear auth token
      setAuthToken(null);
    }

    setIsLoading(false);
  };

  const login = () => {
    authService.login();
  };

  const logout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
    setAuthToken(null);
    window.location.href = '/login';
  };

  const getAccessToken = () => {
    return authService.getAccessToken();
  };

  const refreshAuth = () => {
    checkAuth();
  };

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    getAccessToken,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

